import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { 
  Plus, 
  Trash2, 
  Edit, 
  Loader2, 
  GraduationCap,
  Heart,
  Brain,
  Target,
  BookOpen,
  Sparkles,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIAS = [
  { value: 'CUERPO', label: 'Cuerpo', icon: Heart, color: 'text-red-400' },
  { value: 'MENTE', label: 'Mente', icon: Brain, color: 'text-purple-400' },
  { value: 'PROPOSITO', label: 'Propósito', icon: Target, color: 'text-amber-400' },
];

const PildorasProgresivasPage = () => {
  const [pildoras, setPildoras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [expandedPildora, setExpandedPildora] = useState(null);
  
  const [formData, setFormData] = useState({
    numero_orden: '',
    titulo: '',
    contenido: '',
    categoria: 'CUERPO',
    referencias: ''
  });

  useEffect(() => {
    fetchPildoras();
  }, []);

  const fetchPildoras = async () => {
    try {
      const response = await axios.get(`${API}/admin/pildoras-progresivas`);
      setPildoras(response.data.pildoras || []);
    } catch (error) {
      console.error('Error fetching pildoras:', error);
      toast.error('Error al cargar píldoras');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.numero_orden || !formData.titulo || !formData.contenido) {
      toast.error('Completa todos los campos obligatorios');
      return;
    }

    setSubmitting(true);
    try {
      if (editing) {
        await axios.put(`${API}/admin/pildoras-progresivas/${editing.id}`, {
          numero_orden: parseInt(formData.numero_orden),
          titulo: formData.titulo,
          contenido: formData.contenido,
          categoria: formData.categoria,
          referencias: formData.referencias || null
        });
        toast.success('Píldora actualizada');
      } else {
        await axios.post(`${API}/admin/pildoras-progresivas`, {
          numero_orden: parseInt(formData.numero_orden),
          titulo: formData.titulo,
          contenido: formData.contenido,
          categoria: formData.categoria,
          referencias: formData.referencias || null
        });
        toast.success('Píldora creada');
      }
      setShowDialog(false);
      setEditing(null);
      resetForm();
      fetchPildoras();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta píldora?')) return;
    
    try {
      await axios.delete(`${API}/admin/pildoras-progresivas/${id}`);
      toast.success('Píldora eliminada');
      fetchPildoras();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleEdit = (pildora) => {
    setEditing(pildora);
    setFormData({
      numero_orden: pildora.numero_orden.toString(),
      titulo: pildora.titulo,
      contenido: pildora.contenido,
      categoria: pildora.categoria,
      referencias: pildora.referencias || ''
    });
    setShowDialog(true);
  };

  const handleSeedInicial = async () => {
    setSeeding(true);
    try {
      const response = await axios.post(`${API}/admin/pildoras-progresivas/seed-inicial`);
      toast.success(response.data.message);
      fetchPildoras();
    } catch (error) {
      toast.error('Error al crear píldoras iniciales');
    } finally {
      setSeeding(false);
    }
  };

  const resetForm = () => {
    setFormData({
      numero_orden: pildoras.length > 0 ? (Math.max(...pildoras.map(p => p.numero_orden)) + 1).toString() : '1',
      titulo: '',
      contenido: '',
      categoria: 'CUERPO',
      referencias: ''
    });
  };

  const openNewDialog = () => {
    setEditing(null);
    resetForm();
    setShowDialog(true);
  };

  const getCategoriaInfo = (categoria) => {
    return CATEGORIAS.find(c => c.value === categoria) || CATEGORIAS[0];
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6 p-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground flex items-center gap-3">
              <GraduationCap className="h-7 w-7 text-emerald-400" />
              Píldoras Progresivas
            </h1>
            <p className="text-muted-foreground mt-1">
              Contenido didáctico que cada cliente avanza individualmente
            </p>
          </div>
          <div className="flex items-center gap-3">
            {pildoras.length === 0 && (
              <Button 
                onClick={handleSeedInicial}
                disabled={seeding}
                variant="outline"
                className="border-emerald-500/50 text-emerald-400"
              >
                {seeding ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
                Crear Set Inicial
              </Button>
            )}
            <Button onClick={openNewDialog} className="bg-emerald-600 hover:bg-emerald-700">
              <Plus className="h-4 w-4 mr-2" />
              Nueva Píldora
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4">
          <Card className="bg-card border-border">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-emerald-400">{pildoras.length}</p>
              <p className="text-sm text-muted-foreground">Total Píldoras</p>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-red-400">
                {pildoras.filter(p => p.categoria === 'CUERPO').length}
              </p>
              <p className="text-sm text-muted-foreground">Cuerpo</p>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-purple-400">
                {pildoras.filter(p => p.categoria === 'MENTE').length}
              </p>
              <p className="text-sm text-muted-foreground">Mente</p>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-amber-400">
                {pildoras.filter(p => p.categoria === 'PROPOSITO').length}
              </p>
              <p className="text-sm text-muted-foreground">Propósito</p>
            </CardContent>
          </Card>
        </div>

        {/* Lista de Píldoras */}
        {pildoras.length === 0 ? (
          <Card className="bg-card border-dashed border-border">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <GraduationCap className="h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground text-center">
                No hay píldoras creadas.<br />
                Crea el set inicial o añade píldoras manualmente.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {pildoras.map((pildora) => {
              const catInfo = getCategoriaInfo(pildora.categoria);
              const CatIcon = catInfo.icon;
              const isExpanded = expandedPildora === pildora.id;
              
              return (
                <Card key={pildora.id} className="bg-card border-border">
                  <CardContent className="p-0">
                    {/* Header colapsable */}
                    <div 
                      className="p-4 flex items-center gap-4 cursor-pointer hover:bg-zinc-800/50 transition-colors"
                      onClick={() => setExpandedPildora(isExpanded ? null : pildora.id)}
                    >
                      {/* Número de orden */}
                      <div className="w-12 h-12 flex items-center justify-center bg-emerald-500/20 text-emerald-400 rounded-lg font-bold text-xl">
                        {pildora.numero_orden}
                      </div>
                      
                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium flex items-center gap-1 ${catInfo.color} bg-opacity-20`}
                                style={{ backgroundColor: `${catInfo.color.replace('text-', '')}20` }}>
                            <CatIcon className="h-3 w-3" />
                            {catInfo.label}
                          </span>
                        </div>
                        <h3 className="font-semibold text-foreground truncate">{pildora.titulo}</h3>
                        {!isExpanded && (
                          <p className="text-sm text-muted-foreground truncate mt-1">
                            {pildora.contenido.replace(/\*\*/g, '').substring(0, 80)}...
                          </p>
                        )}
                      </div>
                      
                      {/* Actions */}
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => { e.stopPropagation(); handleEdit(pildora); }}
                          className="text-muted-foreground hover:text-foreground"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => { e.stopPropagation(); handleDelete(pildora.id); }}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                        {isExpanded ? <ChevronUp className="h-5 w-5 text-muted-foreground" /> : <ChevronDown className="h-5 w-5 text-muted-foreground" />}
                      </div>
                    </div>
                    
                    {/* Contenido expandido */}
                    {isExpanded && (
                      <div className="px-4 pb-4 border-t border-border pt-4 animate-fade-in">
                        <div className="prose prose-invert prose-sm max-w-none pl-16">
                          {pildora.contenido.split('\n\n').map((paragraph, i) => (
                            <p key={i} className="text-sm text-foreground/90 mb-3">
                              {paragraph.split('**').map((part, j) => 
                                j % 2 === 1 ? <strong key={j} className="text-emerald-300">{part}</strong> : part
                              )}
                            </p>
                          ))}
                        </div>
                        
                        {pildora.referencias && (
                          <div className="pl-16 mt-4 pt-3 border-t border-border">
                            <p className="flex items-start gap-2 text-xs text-muted-foreground">
                              <BookOpen className="h-3 w-3 mt-0.5 flex-shrink-0" />
                              <span className="italic">{pildora.referencias}</span>
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Dialog para crear/editar */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <GraduationCap className="h-5 w-5 text-emerald-400" />
                {editing ? 'Editar Píldora' : 'Nueva Píldora Progresiva'}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Número de orden *</Label>
                  <Input
                    type="number"
                    min="1"
                    value={formData.numero_orden}
                    onChange={(e) => setFormData({ ...formData, numero_orden: e.target.value })}
                    placeholder="1"
                    className="bg-zinc-900/50 border-zinc-800"
                  />
                  <p className="text-xs text-muted-foreground">
                    Define el orden de progresión (1 = primera píldora)
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label>Categoría *</Label>
                  <Select
                    value={formData.categoria}
                    onValueChange={(value) => setFormData({ ...formData, categoria: value })}
                  >
                    <SelectTrigger className="bg-zinc-900/50 border-zinc-800">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIAS.map((cat) => (
                        <SelectItem key={cat.value} value={cat.value}>
                          <div className="flex items-center gap-2">
                            <cat.icon className={`h-4 w-4 ${cat.color}`} />
                            {cat.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Título *</Label>
                <Input
                  value={formData.titulo}
                  onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
                  placeholder="Ej: El músculo crece durante el descanso"
                  className="bg-zinc-900/50 border-zinc-800"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Contenido * (Markdown soportado)</Label>
                <Textarea
                  value={formData.contenido}
                  onChange={(e) => setFormData({ ...formData, contenido: e.target.value })}
                  placeholder="Escribe el contenido didáctico. Usa **texto** para negritas."
                  className="bg-zinc-900/50 border-zinc-800 min-h-[200px] font-mono text-sm"
                />
                <p className="text-xs text-muted-foreground">
                  Usa **texto** para negritas. Separa párrafos con doble salto de línea.
                </p>
              </div>
              
              <div className="space-y-2">
                <Label>Referencias / Fuentes (opcional)</Label>
                <Input
                  value={formData.referencias}
                  onChange={(e) => setFormData({ ...formData, referencias: e.target.value })}
                  placeholder="Ej: Schoenfeld, B.J. (2010). Journal of Strength and Conditioning."
                  className="bg-zinc-900/50 border-zinc-800"
                />
              </div>
            </div>
            
            <DialogFooter className="mt-6">
              <Button variant="outline" onClick={() => setShowDialog(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleSubmit}
                disabled={submitting}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                {editing ? 'Guardar Cambios' : 'Crear Píldora'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default PildorasProgresivasPage;
